import React, { useState } from "react";
import { useStaticQuery, graphql, Link } from "gatsby";
import { FaSearch } from "react-icons/fa"; // Importing magnifier icon from react-icons
import { buildCategoryPath } from "../../utils/path-utils";

interface Article {
  id: string;
  title: string;
  uri: string;
  type: "post" | "point" | "category" | "tag"; // Add tag type
  categories?: { nodes: { slug: string; databaseId: number; parentDatabaseId?: number; }[] };
  icon?: string; // Optional icon path for tags
}

/**
 * SearchBar Component
 * 
 * This component fetches its own articles and handles search functionality.
 * It is self-contained and does not depend on external data.
 */
const SearchBar: React.FC = () => {
  // GraphQL query to fetch articles
  const data = useStaticQuery(graphql`
    query ArticlesQuery {
      allWpPost {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
        }
      }
      allWpPoint {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
        }
      }
      allWpCategory {
        nodes {
          id
          name
          uri
          slug
          databaseId
          parentDatabaseId
        }
      }
      allWpTag {
        nodes {
          id
          name
          uri
          slug
        }
      }
    }
  `);

  const articles: Article[] = [
    ...data.allWpPost.nodes.map(post => ({ ...post, type: "post" })),
    ...data.allWpPoint.nodes.map(point => ({
      ...point,
      uri: point.uri.replace("/point/", "/"), // Remove /point/ from URI
      type: "point",
    })),
    ...data.allWpCategory.nodes.map(category => ({
      id: category.id,
      title: category.name,
      uri: category.uri.replace("/category/", "/"), // Remove /category/ from URI
      type: "category",
    })),
    ...data.allWpTag.nodes.map(tag => ({
      id: tag.id,
      title: tag.name,
      uri: tag.uri,
      type: "tag",
      icon: `/images/icons/${tag.slug}.svg`, // Construct icon path
    })),
  ]; // Articles fetched via GraphQL
  const allCategories = data.allWpCategory.nodes; // All categories fetched via GraphQL

  const [searchTerm, setSearchTerm] = useState(""); // Search input state
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]); // Filtered search results
  const [isFocused, setIsFocused] = useState(false); // Search input focus state
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Handles the search functionality
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term) {
      // Filter articles based on the search term
      const results = articles.filter((article) =>
        article.title.toLowerCase().includes(term)
      );
      setFilteredArticles(results);
    } else {
      setFilteredArticles([]);
    }
  };

  // Clears the search input and results
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredArticles([]);
    setIsFocused(false);
  };

  // Handles the blur event for the search input
  const handleBlur = () => {
    setTimeout(() => {
      clearSearch(); // Delay to allow click on results before dismissing
    }, 150);
  };

  // Handles keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      // Move down the list
      setHighlightedIndex((prevIndex) =>
        Math.min(prevIndex + 1, filteredArticles.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      // Move up the list
      setHighlightedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      // Navigate to the selected article
      const selectedArticle = filteredArticles[highlightedIndex];
      if (selectedArticle) {
        if (typeof window !== 'undefined') {
          window.location.href = selectedArticle.uri;
        }
      }
    }
  };

  return (
    <div className="search-container">
      <div className="input-wrapper">
        {/* Magnifier Icon */}
        <span className="magnifier-icon" aria-hidden="true">
          <FaSearch />
        </span>

        {/* Search Input */}
        <input
          type="text"
          placeholder="Search articles..."
          value={searchTerm}
          id="search"
          name="search"
          aria-label="Search articles"
          aria-controls="search-results"
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          style={{
    background: "#053800",
    fontWeight: 600,
    border: "1px solid #053800",
    color: "#fff",           
    outline: "none",
  }}
        />
      </div>

      {/* Clear Button */}
      {searchTerm && (
        <button
          className="clear-button"
          onClick={clearSearch}
          aria-label="Clear search"
        >
          ✕
        </button>
      )}

      {/* Search Results */}

      <ul
        id="search-results"
        className="search-results"
        role="listbox"
        aria-label="Search results"
      > {isFocused && filteredArticles.length > 0 && (
        <>
          {filteredArticles.map((article, index) => (
            <li
              key={article.id}
              role="option"
              aria-selected={highlightedIndex === index}
              tabIndex={-1}
              className={`result-item ${highlightedIndex === index ? "highlighted" : ""
                }`}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <Link to={article.type === "category" || article.type === "tag" ? article.uri : (article.categories && article.categories.nodes.length > 0 ? `/${buildCategoryPath(article.categories.nodes[0], allCategories)}${article.uri}` : article.uri)}>
                {article.icon && <img src={article.icon} alt="icon" style={{ width: "16px", height: "16px", marginRight: "8px" }} />}
                {article.title}
              </Link>
            </li>
          ))}              </>
      )}
      </ul>

    </div>
  );
};

export default SearchBar;
